// What the signer has typed, kept on their own device until they finish
// (Nic 2026-10-24). Nothing reaches the server before Finish, and iPhones
// reload background tabs and never show the "leave this page?" warning, so a
// signer who switched apps to look something up came back to empty boxes.
//
// Never kept: signatures (they sign again; it's quick) and anything marked
// sensitive (an SSN or EIN). Kept per signing session for 30 days at most,
// and cleared the moment the packet is submitted.

const KEY_PREFIX = 'vr-sign-draft:';
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const keyOf = (sessionId) => `${KEY_PREFIX}${sessionId}`;

// Only plain answers to fields the documents still have, nothing sensitive.
const keepable = (placements) => {
  const ok = new Map();
  (placements || []).forEach((p) => {
    if (!p || !p.fieldId) return;
    if (p.type === 'signature' || p.sensitive) return;
    ok.set(p.fieldId, p);
  });
  return ok;
};

const plainValue = (v) =>
  typeof v === 'string' ? v.length <= 4000 : typeof v === 'boolean' || typeof v === 'number';

/** valuesByEnv -> what may be stored, per envelope. */
export const draftOf = (valuesByEnv, documents) => {
  const out = {};
  (documents || []).forEach((d) => {
    if (!d || d.completed) return;
    const ok = keepable(d.fieldPlacements);
    const vals = (valuesByEnv || {})[d.envelopeId] || {};
    const kept = {};
    Object.entries(vals).forEach(([fieldId, v]) => {
      if (ok.has(fieldId) && plainValue(v)) kept[fieldId] = v;
    });
    if (Object.keys(kept).length) out[d.envelopeId] = kept;
  });
  return out;
};

export const saveSignDraft = (sessionId, valuesByEnv, documents) => {
  if (!sessionId) return;
  try {
    const values = draftOf(valuesByEnv, documents);
    if (!Object.keys(values).length) {
      window.localStorage.removeItem(keyOf(sessionId));
      return;
    }
    window.localStorage.setItem(keyOf(sessionId), JSON.stringify({ savedAt: Date.now(), values }));
  } catch (_) {
    /* storage full or blocked (private mode): the page still works */
  }
};

/** The saved answers for this session's open documents, or null. */
export const loadSignDraft = (sessionId, documents) => {
  if (!sessionId) return null;
  try {
    const raw = window.localStorage.getItem(keyOf(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.values) return null;
    if (!parsed.savedAt || Date.now() - parsed.savedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(keyOf(sessionId));
      return null;
    }
    // Re-filter against today's documents: a field that's gone, signed or
    // now sensitive is dropped.
    const values = draftOf(parsed.values, documents);
    return Object.keys(values).length ? values : null;
  } catch (_) {
    return null;
  }
};

export const clearSignDraft = (sessionId) => {
  if (!sessionId) return;
  try {
    window.localStorage.removeItem(keyOf(sessionId));
  } catch (_) {
    /* nothing to clear */
  }
};
